import { z } from "zod";
/**
 * Folders that file contracts, and teams that group people.
 *
 * The two are deliberately the same shape. They are separate hierarchies with
 * separate screens, but the tree arithmetic (`hierarchy.ts`) is shared, and
 * keeping the records symmetrical is what lets one set of helpers serve both.
 *
 * Neither carries permissions. Granting a team access to a folder is the
 * obvious next step and is out of scope here; both records carry a stable id
 * and an `orgDomain`, so an ACL can be added later as its own item type
 * without migrating anything written now.
 */
/** Long enough for a descriptive Japanese name, short enough to render in a tree. */
export const MAX_NODE_NAME_LENGTH = 100;
const NameSchema = z.string().trim().min(1).max(MAX_NODE_NAME_LENGTH);
const TimestampSchema = z.string().datetime();
const NodeIdSchema = z.string().min(1);
/**
 * `parentFolderId` is nullable rather than optional: "this is a root folder"
 * has to be a stated fact, not an absent key, or a write that lost the field
 * would read back as a root and quietly reshape the tree.
 */
export const FolderRecordSchema = z.object({
    folderId: NodeIdSchema,
    orgDomain: z.string().min(1),
    parentFolderId: NodeIdSchema.nullable(),
    name: NameSchema,
    createdBy: z.string().email(),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const TeamRecordSchema = z.object({
    teamId: NodeIdSchema,
    orgDomain: z.string().min(1),
    parentTeamId: NodeIdSchema.nullable(),
    name: NameSchema,
    createdBy: z.string().email(),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
/**
 * Membership is its own item rather than a list on the team.
 *
 * A list would make two people added at the same moment a lost update, and it
 * would cap a team at the 400KB item limit. A row per member also gives the
 * "which teams is this person on" query somewhere to hang an index.
 */
export const TeamMemberRecordSchema = z.object({
    teamId: NodeIdSchema,
    email: z.string().email(),
    addedBy: z.string().email(),
    addedAt: TimestampSchema,
});
/**
 * Someone who has signed in at least once.
 *
 * The row is created lazily on the first authenticated request the SPA makes
 * (`GET /me`), not by a Cognito trigger. A trigger fires once at sign-up and
 * would therefore never fire for anyone already in the pool, leaving the
 * directory permanently empty for existing staff; a lazy upsert picks them up
 * the next time they open the app and repairs itself if a row is ever lost.
 *
 * `displayName` is nullable rather than optional for the same reason the
 * parent links are: "Google sent no name" is a fact worth stating, and an
 * absent key would be indistinguishable from a write that dropped it.
 */
export const UserRecordSchema = z.object({
    email: z.string().email(),
    orgDomain: z.string().min(1),
    displayName: z.string().min(1).nullable(),
    /** First sign-in. Never overwritten once set. */
    createdAt: TimestampSchema,
    lastSignInAt: TimestampSchema,
});
/**
 * Why somebody is an administrator. Both can hold at once, and the screens
 * show them apart: the allowlist is fixed by the deployment, the admin team is
 * changed from the user screen.
 */
export const ADMIN_SOURCES = ["ALLOWLIST", "ADMIN_TEAM"];
export function toFolderNode(folder) {
    return {
        id: folder.folderId,
        parentId: folder.parentFolderId,
        name: folder.name,
    };
}
export function toTeamNode(team) {
    return { id: team.teamId, parentId: team.parentTeamId, name: team.name };
}
//# sourceMappingURL=organisation.js.map